import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Input, Collapse, Form, InputNumber, notification, Tooltip, Modal, Spin } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button as CrceButton } from '@crc-evans/ui-components';
import { Info, Factory, Save, Loader2 } from 'lucide-react';
import ProductionJointsModal from './ProductionJointsModal';
import moment from 'moment';
import { connect } from 'react-redux';
import './TechnicianLogSection.css';
import './ProductionJointsSection.css';
import {
    fetchProductionLog,
    updateProductionLog,
    upsertProductionItem,
    deletePipeGroup,
    updateDsrLogSections,
    deleteProductionJoint,
    updatePipeGroup,
    updatePipeData,
} from '../../redux/dsr/action';

const { Text } = Typography;
const { Panel } = Collapse;
const { confirm } = Modal;

const PipeStringRow = ({ pipeString, onEdit, onView, onDelete, onChange, disabled }) => {
    const totalJoints = ((pipeString.lastJointValue || 0) - (pipeString.firstJointValue || 0) + 1);
    const isError = totalJoints > 0 && ((pipeString.repair || 0) + (pipeString.accepted || 0) + (pipeString.rejected || 0) !== totalJoints);

    const renderHeader = () => (
        <div className="modern-dsr-pj-legacy-header-bar">
            <div className="modern-dsr-pj-block-head-left">
                <Text strong>{pipeString.pipeDescription || 'Unnamed Pipe String'}</Text>
            </div>
            <div className="modern-dsr-pj-meta" style={{ alignItems: 'center' }}>
                <span>
                    Pipe String ID <strong>{pipeString.pipeStringID || 'N/A'}</strong>
                </span>
                <Tooltip title="View Info">
                    <button
                        type="button"
                        className="modern-dsr-pj-info-btn"
                        onClick={(e) => { e.stopPropagation(); onView(pipeString); }}
                        disabled={disabled}
                        aria-label="View info"
                    >
                        <Info size={16} strokeWidth={2} />
                    </button>
                </Tooltip>
            </div>
        </div>
    );

    return (
        <Collapse ghost className="modern-dsr-pj-legacy-row-collapse">
            <Panel header={renderHeader()} key={pipeString.id}>
                <div className="modern-dsr-pj-legacy-panel-body">
                    <div className="modern-dsr-pj-fields">
                        <div className="modern-dsr-pj-field">
                            <Form.Item label="First Joint">
                                <InputNumber
                                    className="modern-dsr-tech-inputnum"
                                    min={0}
                                    defaultValue={pipeString.firstJointValue || 0}
                                    onChange={(value) => onChange(pipeString.id, 'firstJointValue', value)}
                                    disabled={disabled}
                                />
                            </Form.Item>
                        </div>
                        <div className="modern-dsr-pj-field">
                            <Form.Item label="Last Joint">
                                <InputNumber
                                    className="modern-dsr-tech-inputnum"
                                    min={0}
                                    defaultValue={pipeString.lastJointValue || 0}
                                    onChange={(value) => onChange(pipeString.id, 'lastJointValue', value)}
                                    disabled={disabled}
                                />
                            </Form.Item>
                        </div>
                        <div className="modern-dsr-pj-field">
                            <Form.Item label="Repairs">
                                <InputNumber
                                    className="modern-dsr-tech-inputnum"
                                    min={0}
                                    defaultValue={pipeString.repair || 0}
                                    onChange={(value) => onChange(pipeString.id, 'repair', value)}
                                    disabled={disabled}
                                />
                            </Form.Item>
                        </div>
                        <div className="modern-dsr-pj-field">
                            <Form.Item label="Accepted">
                                <InputNumber
                                    className="modern-dsr-tech-inputnum"
                                    min={0}
                                    defaultValue={pipeString.accepted || 0}
                                    onChange={(value) => onChange(pipeString.id, 'accepted', value)}
                                    disabled={disabled}
                                />
                            </Form.Item>
                        </div>
                        <div className="modern-dsr-pj-field">
                            <Form.Item label="Rejected">
                                <InputNumber
                                    className="modern-dsr-tech-inputnum"
                                    min={0}
                                    defaultValue={pipeString.rejected || 0}
                                    onChange={(value) => onChange(pipeString.id, 'rejected', value)}
                                    disabled={disabled}
                                />
                            </Form.Item>
                        </div>
                    </div>
                    {isError && (
                        <span className="modern-dsr-pj-error">
                            The sum of Repairs, Accepted, and Rejected must be equal to Total Joints ({totalJoints})
                        </span>
                    )}
                </div>
            </Panel>
        </Collapse>
    );
};

const NewPipeStringRow = ({ pipeString, onEdit, onView, onDelete, onChange, sowId, disabled }) => {
  const totalJoints = (pipeString.lastJointValue || 0) - (pipeString.firstJointValue || 0) + 1;
  const isError =
    totalJoints > 0 &&
    (pipeString.repair || 0) + (pipeString.accepted || 0) + (pipeString.rejected || 0) !==
      totalJoints;

  return (
    <div className="modern-dsr-pj-block">
      <div className="modern-dsr-pj-block-head">
        <div className="modern-dsr-pj-block-head-left">
          <h4 className="modern-dsr-pj-pipe-name">
            {pipeString.pipeDescription || 'Unnamed Pipe String'}
          </h4>
          <div className="modern-dsr-pj-meta">
            <span>
              Scope: <strong>{pipeString.scope || '0'}</strong>
            </span>
            <span>
              Cumulative Till Date: <strong>{pipeString.cumulative || '0'}</strong>
            </span>
            <span>
              Pipe String ID: <strong>{pipeString.pipeStringID || 'N/A'}</strong>
            </span>
            <Tooltip title="View Info">
              <button
                type="button"
                className="modern-dsr-pj-info-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onView(pipeString);
                }}
                disabled={disabled}
                aria-label="View info"
              >
                <Info size={16} strokeWidth={2} />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
      <div className="modern-dsr-pj-fields">
        <div className="modern-dsr-pj-field">
          <Form.Item label="First Joint">
            <InputNumber
              className="modern-dsr-tech-inputnum"
              min={0}
              defaultValue={pipeString.firstJointValue || 0}
              onChange={(value) => {
                onChange(pipeString.id, 'firstJointValue', value);
              }}
              disabled={disabled}
            />
          </Form.Item>
        </div>
        <div className="modern-dsr-pj-field">
          <Form.Item label="Last Joint">
            <InputNumber
              className="modern-dsr-tech-inputnum"
              min={0}
              defaultValue={pipeString.lastJointValue || 0}
              onChange={(value) => {
                onChange(pipeString.id, 'lastJointValue', value);
              }}
              disabled={disabled}
            />
          </Form.Item>
        </div>
        <div className="modern-dsr-pj-field">
          <Form.Item label="Repairs">
            <InputNumber
              className="modern-dsr-tech-inputnum"
              min={0}
              defaultValue={pipeString.repair || 0}
              onChange={(value) => onChange(pipeString.id, 'repair', value)}
              disabled={disabled}
            />
          </Form.Item>
        </div>
        <div className="modern-dsr-pj-field">
          <Form.Item label="Accepted">
            <InputNumber
              className="modern-dsr-tech-inputnum"
              min={0}
              defaultValue={pipeString.accepted || 0}
              onChange={(value) => onChange(pipeString.id, 'accepted', value)}
              disabled={disabled}
            />
          </Form.Item>
        </div>
        <div className="modern-dsr-pj-field">
          <Form.Item label="Rejected">
            <InputNumber
              className="modern-dsr-tech-inputnum"
              min={0}
              defaultValue={pipeString.rejected || 0}
              onChange={(value) => onChange(pipeString.id, 'rejected', value)}
              disabled={disabled}
            />
          </Form.Item>
        </div>
      </div>

      {isError && (
        <span className="modern-dsr-pj-error">
          The sum of Repairs, Accepted, and Rejected must equal Total Joints ({totalJoints})
        </span>
      )}
    </div>
  );
};


const ProductionJointsSection = ({
    shift, selectedDate, onComplete, onCancel, selectedProject, selectedMainline, currentDsrLog,
    // Redux
    currentProductionLog, isUpdatingProductionLog, isProductionLogLoading,
    onFetchProductionLog, onUpdateProductionLog, onUpsertProductionItem, onDeletePipeGroup, onUpdateDsrLogSections, onDeleteProductionJoint, onUpdatePipeGroup, onUpdatePipeData,
    isSkipped, isSubmitted, isShiftSkipped = false, onSkipChange
}) => {
    const [skipped, setSkipped] = useState(false);
    const [comments, setComments] = useState('');
    const [pipeGroups, setPipeGroups] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalConfig, setModalConfig] = useState({ mode: '', data: {}, context: {} });
    const [isDirty, setIsDirty] = useState(false);

    const getShiftNumber = (shiftStr) => (shiftStr === 'day' ? 0 : 1);
    const shiftKey = shift === 'day' ? 'isProductionJointSkippedDayShift' : 'isProductionJointSkippedNightShift';
    const displayDate = moment(selectedDate).format('DD MMM YYYY');

    const getRefreshParams = () => ({
        projectId: selectedProject.id,
        logDate: moment(selectedDate).format('YYYY-MM-DD'),
        userEmail: JSON.parse(localStorage.getItem('user')).email,
        shift: getShiftNumber(shift),
        sowId: selectedMainline.id
    });


    const fetchLogData = useCallback(() => {
        if (selectedProject && selectedMainline && selectedDate && currentDsrLog) {
            onFetchProductionLog({
                projectId: selectedProject.id,
                logDate: moment(selectedDate).format('YYYY-MM-DD'),
                userEmail: JSON.parse(localStorage.getItem('user')).email,
                shift: getShiftNumber(shift),
                sowId: selectedMainline.id,
            });
        }
    }, [selectedProject, selectedMainline, selectedDate, currentDsrLog, shift, onFetchProductionLog]);

    useEffect(() => {
        fetchLogData();
    }, [fetchLogData]);

    // Update skip state from props
    useEffect(() => {
        setSkipped(isSkipped || false);
    }, [isSkipped]);

    // Reset dirty state when date changes (new data should be loaded)
    useEffect(() => {
        setIsDirty(false);
    }, [selectedDate]);

    // Reset skip state when section or shift changes
    useEffect(() => {
        setSkipped(isSkipped || false);
    }, [isSkipped, shift, selectedDate]);

    // Clear local state when component unmounts
    useEffect(() => {
        return () => {
            setPipeGroups([]);
            setComments('');
            setSkipped(false);
            setIsDirty(false);
            setIsModalVisible(false);
        };
    }, []);

    useEffect(() => {
        if (currentProductionLog) {
            // Filter out groups with null pipeGroup and transform the data
            const transformedData = currentProductionLog
                .filter(group => group.pipeGroup !== null) // Filter out null pipe groups
                .map(group => {
                    // Filter production_joint items for current shift and with valid data
                    const filteredPipeStrings = (group.production_joint || [])
                        .filter(ps => {
                            // Filter by current shift (0 for day, 1 for night)
                            const currentShift = getShiftNumber(shift);
                            return Number(ps.shift) === Number(currentShift) || ps.shift === null;
                        })
                        .map(ps => ({
                            ...ps,
                            // Provide default values for null fields
                            firstJointValue: ps.firstJointValue || 0,
                            lastJointValue: ps.lastJointValue || 0,
                            repair: ps.repair || 0,
                            accepted: ps.accepted || 0,
                            rejected: ps.rejected || 0,
                            pipeDescription: ps.pipeDescription || '',
                            factoryCoating: ps.factoryCoating || '',
                            fjcType: ps.fjcType || '',
                            fjcThicknessValue: ps.fjcThicknessValue || 0,
                            fjcThicknessUnit: ps.fjcThicknessUnit || '',
                            pipeStringID: ps.pipeStringID || '',
                            scope: ps.scope || 0,
                            // Ensure parentId is set to the group ID (pipeGroupId)
                            parentId: group.pipeGroupId || group.id || ps.parentId
                        }));

                    return {
                        ...group,
                        id: group.pipeGroupId || group.id || `group-${Math.random()}`, // Use pipeGroupId as primary ID, fallback to id
                        name: group.pipeGroup, // For modal display
                        pipeStrings: filteredPipeStrings,
                        scope: group.scope || 0
                    };
                });

            setPipeGroups(transformedData);

            // Debug log to show the transformed data structure
            console.log('Transformed pipe groups:', transformedData);

            // Get comments from the first valid group
            const firstGroup = currentProductionLog.find(group => group.pipeGroup !== null) || {};
            setComments(shift === 'day' ? (firstGroup.dayShiftComments || '') : (firstGroup.nightShiftComments || ''));
            setIsDirty(false);
        } else {
            setPipeGroups([]);
            setComments('');
        }
    }, [currentProductionLog, shift]);

    const showModal = (mode, data = {}, context = {}) => {
        console.log('showModal called with:', { mode, data, context });

        let initialData;
        if (mode.includes('Group')) {
            initialData = {
                ...data,
                name: data.pipeGroup || '',
                scope: data.scope || 0
            };
        } else if (mode.includes('PipeData')) {
            // Map API response fields to modal form fields with proper null handling
            initialData = {
                ...data,
                // Map fjcThicknessValue to fjcThickness for the form
                fjcThickness: data.fjcThicknessValue || 0,
                // Map fjcThicknessUnit to unit for the form
                unit: data.fjcThicknessUnit || '',
                // Map pipeStringID to pipeStringId for the form
                pipeStringId: data.pipeStringID || '',
                // Keep other fields as they are with null handling
                pipeDescription: data.pipeDescription || '',
                factoryCoating: data.factoryCoating || '',
                fjcType: data.fjcType || '',
                scope: data.scope || 0,
                // Ensure other required fields have defaults
                firstJointValue: data.firstJointValue || 0,
                lastJointValue: data.lastJointValue || 0,
                repair: data.repair || 0,
                accepted: data.accepted || 0,
                rejected: data.rejected || 0
            };
        } else {
            initialData = data;
        }
        setModalConfig({ mode, data: initialData, context });
        setIsModalVisible(true);
    };

    const handleCancel = () => setIsModalVisible(false);

    const handleOk = (values) => {
        const { mode, data, context } = modalConfig;
        let payload;
        const commonPayload = {
            projectId: selectedProject.id,
            dsrDate: moment(selectedDate).format('YYYY-MM-DD'),
            shift: getShiftNumber(shift),
        };

        if (mode === 'addGroup') {
            payload = {
                ...commonPayload,
                pipeGroup: values.name || '',
                scope: values.scope || 0,
                isGroup: true
            };

            console.log('Adding group with payload:', { productionJoint: [payload] });
            console.log('Full payload structure:', {
                productionJoint: [payload],
                projectId: selectedProject.id,
                logDate: moment(selectedDate).format('YYYY-MM-DD'),
                userEmail: JSON.parse(localStorage.getItem('user')).email,
                shift: getShiftNumber(shift),
                sowId: selectedMainline.id
            });

            onUpsertProductionItem({
                productionJoint: [payload],
            // Pass params to refresh data after upsert
                ...{ projectId: selectedProject.id, logDate: moment(selectedDate).format('YYYY-MM-DD'), userEmail: JSON.parse(localStorage.getItem('user')).email, shift: getShiftNumber(shift), sowId: selectedMainline.id }
            });
        } else if (mode === 'editGroup') {
            // Use the new updatePipeGroup action for editing pipe groups
            console.log('Editing group with ID:', data.id, 'Group data:', data);

            onUpdatePipeGroup({
                id: data.id,
                pipeGroupData: {
                    scope: values.scope || 0,
                    pipeGroup: values.name || ''
                },
                projectId: selectedProject.id,
                logDate: moment(selectedDate).format('YYYY-MM-DD'),
                userEmail: JSON.parse(localStorage.getItem('user')).email,
                shift: getShiftNumber(shift),
                sowId: selectedMainline.id
            });
        } else if (mode === 'addPipeData') {
            // Map form values to API format for new pipe data
            const apiPayload = {
                ...commonPayload,
                parentId: context.groupId,
                isChild: true,
                // Map form fields to API fields with null handling
                pipeDescription: values.pipeDescription || '',
                factoryCoating: values.factoryCoating || '',
                fjcType: values.fjcType || '',
                fjcThicknessValue: values.fjcThickness || 0,
                fjcThicknessUnit: values.unit || '',
                scope: values.scope || 0,
                pipeStringID: values.pipeStringId || '',
                // Add default values for joint counts
                firstJointValue: values.firstJointValue || 0,
                lastJointValue: values.lastJointValue || 0,
                repair: values.repair || 0,
                accepted: values.accepted || 0,
                rejected: values.rejected || 0
            };

            console.log('Adding pipe data with payload:', { productionJoint: [apiPayload] });
            console.log('Full pipe data payload structure:', {
                productionJoint: [apiPayload],
                projectId: selectedProject.id,
                logDate: moment(selectedDate).format('YYYY-MM-DD'),
                userEmail: JSON.parse(localStorage.getItem('user')).email,
                shift: getShiftNumber(shift),
                sowId: selectedMainline.id
            });

            onUpsertProductionItem({
                productionJoint: [apiPayload],
            // Pass params to refresh data after upsert
                ...{ projectId: selectedProject.id, logDate: moment(selectedDate).format('YYYY-MM-DD'), userEmail: JSON.parse(localStorage.getItem('user')).email, shift: getShiftNumber(shift), sowId: selectedMainline.id }
            });
        } else if (mode === 'editPipeData') {
            // Use the new updatePipeData action for editing pipe data
            if (!context.groupId) {
                notification.error({ message: 'Group ID is required for editing pipe data.' });
                return;
            }

            const pipeData = {
                id: data.id,
                pipeDescription: values.pipeDescription || '',
                factoryCoating: values.factoryCoating || '',
                fjcType: values.fjcType || '',
                fjcThicknessValue: values.fjcThickness || 0,
                fjcThicknessUnit: values.unit || '',
                scope: values.scope || 0,
                parentId: context.groupId, // Use groupId from context instead of data.parentId
                pipeStringID: values.pipeStringId || '',
                projectId: selectedProject.id,
                // Add joint count fields
                firstJointValue: values.firstJointValue || 0,
                lastJointValue: values.lastJointValue || 0,
                repair: values.repair || 0,
                accepted: values.accepted || 0,
                rejected: values.rejected || 0
            };

            console.log('Updating pipe data with:', pipeData);

            onUpdatePipeData({
                id: data.id,
                pipeData,
                projectId: selectedProject.id,
                logDate: moment(selectedDate).format('YYYY-MM-DD'),
                userEmail: JSON.parse(localStorage.getItem('user')).email,
                shift: getShiftNumber(shift),
                sowId: selectedMainline.id
            });
        } else if (mode === 'deletePipeData') {
            // Handle delete pipe data
            onDeleteProductionJoint({
                id: data.id,
                ...getRefreshParams(),
            });
        }

        setIsModalVisible(false);
    };

    const handleDeleteGroup = (groupId) => {
        if (!groupId) {
            notification.error({ message: 'Cannot delete group without valid ID.' });
            return;
        }

        console.log('Deleting group with ID:', groupId);

        confirm({
            title: 'Are you sure you want to delete this pipe group?',
            icon: <ExclamationCircleOutlined />,
            content: 'This will delete the group and all its sub-joints. This action cannot be undone.',
            onOk() {
                onDeletePipeGroup({
                    id: groupId,
        // Pass params to refresh data after delete
                    ...{ projectId: selectedProject.id, logDate: moment(selectedDate).format('YYYY-MM-DD'), userEmail: JSON.parse(localStorage.getItem('user')).email, shift: getShiftNumber(shift), sowId: selectedMainline.id }
                });
            },
        });
    };

    const handlePipeStringChange = (groupId, pipeStringId, field, value) => {
        const updatedGroups = pipeGroups.map(group => {
            if (group.id === groupId) {
                const updatedPipeStrings = group.pipeStrings.map(ps => ps.id === pipeStringId ? { ...ps, [field]: value } : ps);
                return { ...group, pipeStrings: updatedPipeStrings };
            }
            return group;
        });
        setPipeGroups(updatedGroups);
        setIsDirty(true);
    };

    const handleDeletePipeString = (pipeStringId) => {
        if (!pipeStringId) {
            notification.error({ message: 'Cannot delete pipe string without valid ID.' });
            return;
        }
        // Show delete modal instead of confirm dialog
        showModal('deletePipeData', { id: pipeStringId });
    };

    const handleSkipChange = (e) => {
        const newSkipState = e.target.checked;
        setSkipped(newSkipState);
        setIsDirty(true);

        // Use the parent component's centralized skip handler
        if (onSkipChange) {
            onSkipChange(e);
        } else {
            // Fallback to local handling if no parent handler provided
            if (currentDsrLog?.id) {
                const submissionStatus = generateSubmissionStatus('production', shift, true);
                const sectionsSkipped = {
                    ...currentDsrLog.sectionsSkipped,
                    isProductionJointSkippedDayShift: shift === 'day' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isProductionJointSkippedDayShift || 0),
                    isProductionJointSkippedNightShift: shift === 'night' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isProductionJointSkippedNightShift || 0),
                };

                onUpdateDsrLogSections({
                    dsrLogId: currentDsrLog.id,
                    submissionStatus: submissionStatus,
                    sectionsSkipped: sectionsSkipped
                });
            }
        }
    };

    // Helper function to generate submission status fields for a specific section and shift
    const generateSubmissionStatus = (section, shift, isSubmitted = true) => {
        const shiftKeySuffix = shift === 'day' ? 'Dayshift' : 'Nightshift';
        let submittedKey;

        switch (section) {
            case 'technician':
                submittedKey = `isTechnicianLogSubmitted${shiftKeySuffix}`;
                break;
            case 'downtime':
                submittedKey = `isDowntimeSubmitted${shiftKeySuffix}`;
                break;
            case 'consumables':
                submittedKey = `isConsumablesSubmitted${shiftKeySuffix}`;
                break;
            case 'equipment':
                submittedKey = `isEquipmentUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'ehs':
                submittedKey = `isEhsIssuesSubmitted${shiftKeySuffix}`;
                break;
            case 'material':
                submittedKey = `isMaterialUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'production':
                submittedKey = `isProductionJointSubmitted${shiftKeySuffix}`;
                break;
            default:
                return {};
        }

        return { [submittedKey]: isSubmitted };
    };

    const handleSave = async () => {
        if (skipped) {
            // If skipped, just complete without saving data to API
            if (onComplete) onComplete(true, 'production');
            return;
        }

        const dsrLogProductionJoint = [];
        pipeGroups.forEach(group => {
            (group.pipeStrings || []).forEach(ps => {
                // Include all pipe strings that have valid IDs, regardless of shift
                if (ps.id) {
                    console.log('Pipe string data for saving:', ps);

                    dsrLogProductionJoint.push({
                        productionJointId: ps.id,
                        firstJointValue: ps.firstJointValue || 0,
                        lastJointValue: ps.lastJointValue || 0,
                        repair: ps.repair || 0,
                        accepted: ps.accepted || 0,
                        rejected: ps.rejected || 0,
                        shift: ps.shift || getShiftNumber(shift), // Include shift field
                        dsrLogId: ps.dsrLogId || null // Use dsrLogId first, fallback to dsrLogProductionJointId, or null for new entries
                    });
                }
            });
        });

        // Get comments from the first valid group or use existing comments
        const firstGroup = currentProductionLog?.find(group => group.pipeGroup !== null) || {};
        const dayShiftComments = shift === 'day' ? comments : (firstGroup.dayShiftComments || '');
        const nightShiftComments = shift === 'night' ? comments : (firstGroup.nightShiftComments || '');

        console.log('Saving DSR log production joints:', {
            dsrLogProductionJoint,
            dayShiftComments,
            nightShiftComments
        });

        const payload = {
            dsrLogProductionJoint,
            dayShiftComments,
            nightShiftComments,
        // Pass params to refresh data after save
            ...{ projectId: selectedProject.id, logDate: moment(selectedDate).format('YYYY-MM-DD'), userEmail: JSON.parse(localStorage.getItem('user')).email, shift: getShiftNumber(shift), sowId: selectedMainline.id }
        };

        await onUpdateProductionLog(payload);
        setIsDirty(false);

        // Update submission status and sectionsSkipped for this section
        const submissionStatus = generateSubmissionStatus('production', shift, true);
        const sectionsSkipped = {
            ...currentDsrLog.sectionsSkipped,
            isProductionJointSkippedDayShift: shift === 'day' ? 0 : (currentDsrLog.sectionsSkipped?.isProductionJointSkippedDayShift || 0),
            isProductionJointSkippedNightShift: shift === 'night' ? 0 : (currentDsrLog.sectionsSkipped?.isProductionJointSkippedNightShift || 0)
        };

        await onUpdateDsrLogSections({
            dsrLogId: currentDsrLog?.id,
            submissionStatus: submissionStatus,
            sectionsSkipped: sectionsSkipped
        });

        // Call completion handler to navigate to next section immediately
        if (onComplete) onComplete(true, 'production');

        // Refresh data in the background after navigation
        setTimeout(() => {
            if (selectedProject && selectedMainline && selectedDate) {
                const userEmail = JSON.parse(localStorage.getItem('user')).email;
                const logDate = moment(selectedDate).format('YYYY-MM-DD');
                const shiftNumber = shift === 'day' ? '0' : '1';

                onFetchProductionLog({
                    projectId: selectedProject.id,
                    logDate: logDate,
                    userEmail: userEmail,
                    shift: shiftNumber,
                    sowId: selectedMainline.id
                });
            }
        }, 100);
    };

    const handleNext = () => {
        if (isDirty) {
            Modal.confirm({
                title: 'Warning!',
                icon: <ExclamationCircleOutlined />,
                content: 'You have made changes to the table. Do you want to save them?',
                okText: 'Save',
                cancelText: 'Discard',
                onOk: () => {
                    handleSave();
                    if (onComplete) onComplete(!skipped);
                },
                onCancel: () => {
                    if (onComplete) onComplete(!skipped);
                }
            });
        } else {
            if (onComplete) onComplete(!skipped);
        }
    };

    const renderPanelHeader = (group) => (
        <div className="modern-dsr-pj-group-head">
            <span className="modern-dsr-pj-group-head-title">{group.name || 'Unnamed Group'}</span>
            <span className="modern-dsr-pj-group-head-meta">
                Scope<strong>{group.scope || 0}</strong>
            </span>
        </div>
    );

    return (
        <div className="modern-dsr-tech-log-section">
            {isShiftSkipped ? (
                <div className="modern-dsr-tech-log-skipped-banner">
                    <strong>
                        {shift.charAt(0).toUpperCase() + shift.slice(1)} Shift Skipped
                    </strong>
                    <p>
                        This section is disabled because the {shift} shift has been skipped.
                    </p>
                </div>
            ) : (
                <Spin
                    spinning={isProductionLogLoading}
                    indicator={(
                        <span className="modern-dsr-tech-log-spin" aria-hidden>
                            <Loader2 className="modern-dsr-tech-log-spin-icon" size={28} strokeWidth={2} />
                        </span>
                    )}
                    tip="Loading production joints data..."
                    size="large"
                >
                    <div className="modern-dsr-tech-log-card">
                        <div className="modern-dsr-tech-log-card-head">
                            <div className="modern-dsr-tech-log-card-title-block">
                                <div className="modern-dsr-tech-log-card-icon" aria-hidden>
                                    <Factory size={22} strokeWidth={2} />
                                </div>
                                <div>
                                    <h3 className="modern-dsr-tech-log-card-title">Production joints</h3>
                                    <p className="modern-dsr-tech-log-card-sub">Record first and last joint numbers, repairs, and accepted / rejected counts by pipe string.</p>
                                </div>
                            </div>
                        </div>

                        <div className="modern-dsr-pj-list">
                            {Array.isArray(pipeGroups) &&
                pipeGroups.map((group, groupIndex) => {
                  const joints = group.production_joint || [];

                  const hasNew = joints.some(
                    (j) => j.dsrLogId !== null && j.production_status === 'new'
                  );

                  const hasNullStatus =
                    !hasNew &&
                    joints.some((j) => j.dsrLogId !== null && j.production_status === null);

                  if (hasNew) {
                    return (
                      <React.Fragment key={group.pipeGroupId || `group-${groupIndex}`}>
                        {joints
                          .filter((j) => j.dsrLogId !== null && j.production_status === 'new')
                          .map((joint) => (
                            <NewPipeStringRow
                              key={joint.id || `temp-ps-${Math.random()}`}
                              pipeString={joint}
                              onEdit={(data) => {
                                if (!data.id) {
                                  notification.error({
                                    message: 'Cannot edit pipe string without valid ID.',
                                  });
                                  return;
                                }
                                showModal('editPipeData', data, {
                                  groupScope: group.scope,
                                  groupId: group.pipeGroupId,
                                });
                              }}
                              onView={(data) => showModal('viewPipeData', data)}
                              onDelete={(pipeStringId) => handleDeletePipeString(pipeStringId)}
                              onChange={(pipeStringId, field, value) =>
                                handlePipeStringChange(
                                  group.pipeGroupId,
                                  pipeStringId,
                                  field,
                                  value
                                )
                              }
                              sowId={joint.sowId}
                            />
                          ))}
                      </React.Fragment>
                    );
                  }

                  if (hasNullStatus) {
                    return (
                      <Collapse
                        accordion
                        ghost
                        className="modern-dsr-pj-group-collapse"
                        key={group.pipeGroupId || `group-${groupIndex}`}
                      >
                        <Panel
                          header={renderPanelHeader(group)}
                          key={group.pipeGroupId || `temp-${Math.random()}`}
                        >
                          {joints
                            .filter((j) => j.dsrLogId !== null && j.production_status === null)
                            .map((joint) => (
                              <div className="modern-dsr-pj-block-wrap" key={joint.id || `temp-ps-${Math.random()}`}>
                                <PipeStringRow
                                    pipeString={joint}
                                    onEdit={(data) => {
                                  if (!data.id) {
                                    notification.error({
                                      message: 'Cannot edit pipe string without valid ID.',
                                    });
                                    return;
                                  }
                                  showModal('editPipeData', data, {
                                    groupScope: group.scope,
                                    groupId: group.pipeGroupId,
                                  });
                                }}
                                    onView={(data) => showModal('viewPipeData', data)}
                                    onDelete={(pipeStringId) => handleDeletePipeString(pipeStringId)}
                                    onChange={(pipeStringId, field, value) =>
                                  handlePipeStringChange(
                                    group.pipeGroupId,
                                    pipeStringId,
                                    field,
                                    value
                                  )
                                }
                                />
                              </div>
                            ))}
                        </Panel>
                      </Collapse>
                    );
                  }

                  return null;
                })}
                        </div>

                        <div className="modern-dsr-tech-log-issues-block">
                            <div className="modern-dsr-tech-log-issues-label">Comments</div>
                            <Input.TextArea
                                className="modern-dsr-tech-log-issues-textarea"
                                rows={4}
                                value={comments}
                                onChange={(e) => { setComments(e.target.value); setIsDirty(true); }}
                                disabled={skipped || isSubmitted}
                            />
                        </div>
                    </div>
                </Spin>
            )}

            <div className="modern-dsr-tech-log-form-footer" aria-label="Form actions">
                <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={onCancel} />
                <CrceButton
                    label={isUpdatingProductionLog ? 'Saving…' : 'Save & Next'}
                    variant="primary"
                    size="medium"
                    icon={Save}
                    onClick={handleSave}
                    disabled={isUpdatingProductionLog || isSubmitted || isShiftSkipped}
                />
            </div>

            <ProductionJointsModal visible={isModalVisible} mode={modalConfig.mode} initialData={modalConfig.data} groupScope={modalConfig.context.groupScope} onCancel={handleCancel} onOk={handleOk} />
        </div>
    );
};

const mapStateToProps = ({ dsrReducer }) => ({
    currentDsrLog: dsrReducer.currentDsrLog,
    currentProductionLog: dsrReducer.currentProductionLog,
    isUpdatingProductionLog: dsrReducer.isUpdatingProductionLog,
    isProductionLogLoading: dsrReducer.isProductionLogLoading,
    isUpserting: dsrReducer.isUpsertingProductionItem,
    isDeleting: dsrReducer.isDeletingPipeGroup,
    isDeletingJoint: dsrReducer.isDeletingProductionJoint,
});

const mapDispatchToProps = {
    onFetchProductionLog: fetchProductionLog,
    onUpdateProductionLog: updateProductionLog,
    onUpsertProductionItem: upsertProductionItem,
    onDeletePipeGroup: deletePipeGroup,
    onUpdateDsrLogSections: updateDsrLogSections,
    onDeleteProductionJoint: deleteProductionJoint,
    onUpdatePipeGroup: updatePipeGroup,
    onUpdatePipeData: updatePipeData,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductionJointsSection); 